import { Suspense, lazy, useState } from 'react'
import {
  CalendarMinus, CalendarPlus, Download, Heart, HeartOff, ListPlus, MessageSquareWarning, Share2, Star, Ticket, X,
} from 'lucide-react'
import { Banner, Field, Row, Sheet, useToast } from '../ui'
import { PlacedChips, nextShowing, occurrencesOf, showingLabel } from './ShowingChips'
import { RateSheet } from './RateSheet'
import { useSeen } from '../lib/seen'
import { useVenues } from '../lib/queries'
import type { PlaceVenue } from '../lib/sheetPlace'
import './EventActions.css'

/**
 * Lazy, and this is load-bearing rather than tidy.
 *
 * WP 1.1 wanted the admin code out of the main bundle (X10) and got half of it:
 * `Notes` would not split, because `FeedbackButton` — which is on Home —
 * imported `NotesComposer` from the same module, so the `React.lazy` around
 * `Notes` in `AccountSheet` bought nothing. WP 2.3 deleted `FeedbackButton`,
 * which left `VenueAdmin` (already lazy) as the only static importer and
 * finally closed X10.
 *
 * This component is reached from every row in the app, so importing `Notes`
 * statically here would pull it straight back into the main chunk and reopen
 * it. The composer is also only needed by one of six menu items, and only after
 * a tap.
 */
const NotesComposer = lazy(() => import('./Notes').then(m => ({ default: m.NotesComposer })))
import { AddToListSheet } from './AddToListSheet'
import { useAddNote, useMarkWrong, useRemoveCommitment, useScheduleEntry } from '../lib/mutations'
import { attempt, useWriteError } from '../lib/useAction'
import { calendarWindow, idsOf, signalFeatures } from '../lib/entries'
import { icsFor, renderIcs } from '../lib/ics'
import { dayIn, hereZone, midnightIsoIn, zoneOfCity } from '../lib/zone'
import { calendarTapAction } from '../lib/calendarTap'
import { uniqueShowings } from '../lib/places'
import { rowTitle, sharePayload } from '../lib/rowMeta'
import { addDays, iso } from '../lib/calendar'
import type { Commitment, FeedRow } from '../lib/types'
import type { ShowingLite } from './CardBody'

/**
 * Phase 9, D10(i)'s `.ics` download, ported from the deleted
 * `AddToCalendarSheet` rather than dropped with it — this is still the only
 * way to get a showing onto a calendar this app doesn't sync (Doug's other
 * one, a phone's own). `showing` is whichever the menu would otherwise
 * schedule — the chosen one, else the next — and `date` the picked day for a
 * run with nothing clocked. The showing is handed over whole rather than
 * found again by its day (calendar-pick-date): a film at two cinemas has two
 * showings on one day, and a lookup by day returned the first, not the one
 * that was chosen.
 */
function downloadIcs(
  e: FeedRow, showing: ShowingLite | null, date: string, summary: string | null | undefined,
  venueOf: (id: string) => PlaceVenue | null | undefined,
) {
  const ics = renderIcs(icsFor(e, showing, date, summary, venueOf))
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `kiez-${e.id}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Can this device hand a share off to the operating system?
 *
 * E3 asks for Web Share and nothing else — no copy-link fallback and no
 * mailto. Safari on iOS and Chrome on Android have it, a desktop browser
 * mostly does not, and a Share item that opened nothing would be worse than
 * one that is not there. So both the menu and the sheet's action bar ask
 * first. Exported from here because this is the component that owns the
 * action; the sheet's bar calls the same two functions.
 */
export const canShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function'

/** Share, and swallow the abort a dismissed share sheet throws — cancelling is
 *  not an error and must not reach a write-error banner. */
export async function shareEvent(e: FeedRow): Promise<void> {
  if (!canShare()) return
  try { await navigator.share(sharePayload(e, new Date(), e.url)) }
  catch { /* the user dismissed it, or the platform refused the payload */ }
}

/**
 * The long-press menu on a row, and the same set the sheet's overflow opens.
 *
 * Six items, always the same six in the same order, whatever the row is. A
 * menu whose items move depending on the event is a menu you have to read
 * every time, and the one thing a long press is for is not having to.
 * (It has grown past six since. Two items depend on the device or the
 * caller rather than the row: Share, where the platform has one, and — WP
 * 10.2 — Check tickets, from the sheet only.)
 *
 * Built on `ui/Row` rather than on a bespoke menu item, for the reason the
 * primitive exists: this is a list of things you tap, and there is one of
 * those in the app.
 */
export function EventActions({
  e, saved, showings, chosen, commitment, summary, onClose, onSave, onForget, onDismiss, onCheckTickets, stacked = false,
}: {
  e: FeedRow
  saved: boolean
  showings?: ShowingLite[]
  /** calendar-pick-date: the chip the caller's own sheet has tapped, where
   *  it has chips — so More → Add to calendar, or Download .ics, takes the
   *  night the person already pointed at rather than asking again. Absent
   *  from a row's long-press, which has no chips. */
  chosen?: ShowingLite | null
  /** This entry's own row in `commitments`, where one exists — WP 3.3's E1.
   *  Swaps "Add to calendar" for "Remove from the Year"; the button in the
   *  sheet's own action bar reads the same fact. */
  commitment?: Commitment | null
  /** For the `.ics`'s description — `feed_detail`'s, the same one the sheet's
   *  body prints, since this menu has no fetch of its own for it. */
  summary?: string | null
  onClose: () => void
  onSave: () => void
  onForget: () => void
  onDismiss: () => void
  /** WP 10.2: the sheet's ticket check, moved here off its bar. Only the
   *  sheet passes it — the check's answer is drawn in the sheet's own ticket
   *  line, so from a row's long-press there would be nowhere to show it. */
  onCheckTickets?: () => void
  /** UI-audit a2-40: this menu is open on top of another sheet (the event
   *  sheet's More), so it gets the ×. A row's long-press opens it alone. */
  stacked?: boolean
}) {
  const today = iso(new Date())
  const win = calendarWindow(e, today)
  // An undated row can still be put in the diary on a day you choose — the
  // same window the sheet offers, and the reason `schedWin` is not just `win`.
  const schedWin = win ?? (!e.run_start ? { min: today, max: addDays(today, 730) } : null)

  const [view, setView] = useState<'menu' | 'wrong' | 'addlist' | 'rate'>('menu')
  const [pickingDate, setPickingDate] = useState(false)
  const [pickDate, setPickDate] = useState(today)
  // calendar-pick-date: the night the person chose here (seeded from the
  // sheet's own tap), and whether this menu is asking. Two states, as in
  // the sheet: a choice can exist without the question having been asked,
  // and the question can be open before any choice is made.
  const [pick, setPick] = useState<ShowingLite | null>(chosen ?? null)
  const [pickingShowing, setPickingShowing] = useState(false)
  const now = new Date()
  const occurrences = uniqueShowings(occurrencesOf(showings ?? []))
    .filter(s => new Date(s.starts_at).getTime() >= now.getTime())
  const selected = pick ?? nextShowing(showings ?? [], now)
  const tapAction = calendarTapAction({ occurrences: occurrences.length, chosen: pick != null, hasWindow: !!schedWin })
  const schedule = useScheduleEntry()
  // WP 8.4: every entry a merged film card stands for, so rating one week of
  // a run rates the whole run rather than leaving the other weeks to ask
  // again — the same reasoning `forEachEntry` exists for elsewhere in this
  // app. `seenQ` reads the whole table; a card's own row is `e.id`'s, which
  // for a merged card is the representative id `idsOf` always leads with.
  const ids = idsOf(e)
  const seenQ = useSeen()
  const seenRow = seenQ.data?.get(e.id) ?? null
  const seenLabel = !seenRow
    ? 'Seen it · rate'
    : seenRow.stars != null
      ? `Seen · ${'★'.repeat(seenRow.stars)}${'☆'.repeat(5 - seenRow.stars)}`
      : 'Seen · not rated'
  const removeCommitmentM = useRemoveCommitment()
  const wrongM = useMarkWrong()
  const addNote = useAddNote()
  const noteError = useWriteError(addNote)
  const scheduleError = useWriteError(schedule)
  const toast = useToast()
  // UI-audit a1-01: the `.ics` for a night at another cinema than the row's
  // own takes that cinema's street, from the venues the app already holds.
  const { data: venues } = useVenues()
  const venueOf = (id: string) => venues?.byId.get(id)

  /**
   * WP 3.3: the same direct write the sheet's own Calendar button makes —
   * next showing found, no picker — with the inline `Field` below as the
   * fallback for a run with nothing clocked (rule 2: no second sheet from
   * either caller).
   */
  const commit = async (startsAt: string) => {
    try {
      const res = await schedule.mutateAsync({
        entryId: e.id, startsAt, title: e.title, features: signalFeatures(e),
      })
      const id = (res as { data: { id: string } | null }).data?.id ?? null
      toast({
        text: 'Added to the Year',
        action: { label: 'Undo', onClick: () => { if (id) removeCommitmentM.mutate(id) } },
      })
      onClose()
    } catch { /* left on screen: scheduleError reads schedule.error */ }
  }

  /** calendar-pick-date: the same rule as the sheet's bar (`lib/calendarTap.ts`).
   *  Several nights and no choice draws the chips below this row, with an
   *  Add under them, instead of writing the next night unasked. */
  const tapCalendar = () => {
    switch (tapAction) {
      case 'write': if (selected) void commit(selected.starts_at); return
      case 'pick': setPickingShowing(true); return
      case 'date-input': if (schedWin) { setPickDate(schedWin.min); setPickingDate(true) } return
      case 'passed': return
    }
  }

  /** The day `.ics` downloads for: the chosen showing's, else the next's,
   *  the day the inline picker has open, or the start of the window —
   *  always something, since `Field.Input`'s own `min` is `schedWin.min`. */
  const icsDate = (() => {
    if (selected) return dayIn(selected.starts_at, zoneOfCity(e.city))
    if (pickingDate) return pickDate
    return schedWin?.min ?? today
  })()

  /**
   * The composer stacks on top of this menu rather than replacing it — see
   * `EventSheet`'s own note for why. An early return here unmounted Vaul's
   * root mid-flight and left its scrim in the DOM intercepting every pointer
   * event, so the sheet that replaced it drew correctly and could not be
   * tapped.
   *
   * WP 8.9: staying mounted-and-open drew this menu's own grab handle and
   * title underneath RateSheet's — two `.u-sheet`s visibly open at once, the
   * same event title printed twice. `chromeHidden` keeps the workaround
   * (nothing unmounts) while hiding what it used to leave in view, for
   * every `view` this component itself replaces the menu with — "wrong" and
   * the picker both stacked the same way, opinion just never had a
   * duplicate title to make it obvious.
   */
  return (
    <>
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={rowTitle(e)} chromeHidden={view !== 'menu'} closeButton={stacked}>
      <div className="ev-acts">
        <Row
          className="u-row--list"
          leading={saved ? <HeartOff size={20} /> : <Heart size={20} />}
          title={saved ? 'Remove from saved' : 'Save'}
          meta={null}
          onPress={() => { saved ? onForget() : onSave(); onClose() }}
        />
        {commitment ? (
          <Row
            className="u-row--list"
            leading={<CalendarMinus size={20} />}
            title="Remove from the Year"
            meta={null}
            onPress={() => { removeCommitmentM.mutate(commitment.id); onClose() }}
          />
        ) : (
          <Row
            className="u-row--list"
            leading={<CalendarPlus size={20} />}
            title="Add to calendar"
            meta={null}
            disabled={!schedWin}
            onPress={tapCalendar}
          />
        )}
        {pickingShowing && selected && (
          <>
            <div className="ev-acts__showings">
              {scheduleError && <Banner tone="bad">{scheduleError}</Banner>}
              <PlacedChips row={e} occurrences={occurrences} showings={showings ?? []} value={selected} onChange={setPick} now={now} />
            </div>
            {/* Outside the inset block, so it lines up with every other row
                of the menu — a row drawn 48px further in reads as a
                sub-item of the chips, not as the thing to tap next. */}
            <Row
              className="u-row--list"
              leading={<CalendarPlus size={20} />}
              title={`Add ${showingLabel(selected.starts_at, zoneOfCity(e.city))}`}
              meta={occurrences.some(s => s.venue !== selected.venue) ? selected.venue : null}
              disabled={schedule.isPending}
              onPress={() => void commit(selected.starts_at)}
            />
          </>
        )}
        {pickingDate && schedWin && (
          <div className="ev-acts__datepick">
            {scheduleError && <Banner tone="bad">{scheduleError}</Banner>}
            <Field label="Date">
              <Field.Input
                type="date" value={pickDate} min={schedWin.min} max={schedWin.max}
                disabled={schedule.isPending}
                onChange={ev => setPickDate(ev.target.value)}
              />
            </Field>
            <Row
              className="u-row--list"
              leading={<CalendarPlus size={20} />}
              title="Add"
              meta={null}
              disabled={schedule.isPending}
              onPress={() => void commit(midnightIsoIn(pickDate, hereZone()))}
            />
          </div>
        )}
        <Row
          className="u-row--list"
          leading={<Download size={20} />}
          title="Download .ics"
          meta={null}
          onPress={() => { downloadIcs(e, selected, icsDate, summary, venueOf); onClose() }}
        />
        <Row
          className="u-row--list"
          leading={<ListPlus size={20} />}
          title="Add to list"
          meta={null}
          onPress={() => setView('addlist')}
        />
        <Row
          className="u-row--list"
          leading={<Star size={20} fill={seenRow ? 'currentColor' : 'none'} />}
          title={seenLabel}
          meta={null}
          onPress={() => setView('rate')}
        />
        {canShare() && (
          <Row
            className="u-row--list"
            leading={<Share2 size={20} />}
            title="Share"
            meta={null}
            onPress={() => { void shareEvent(e); onClose() }}
          />
        )}
        {onCheckTickets && (
          <Row
            className="u-row--list"
            leading={<Ticket size={20} />}
            title="Check tickets"
            meta={null}
            onPress={() => { onCheckTickets(); onClose() }}
          />
        )}
        <Row
          className="u-row--list"
          leading={<X size={20} />}
          title="Not for me"
          meta={null}
          onPress={() => { onDismiss(); onClose() }}
        />
        <Row
          className="u-row--list"
          leading={<MessageSquareWarning size={20} />}
          title="Something wrong with this?"
          meta={null}
          onPress={() => setView('wrong')}
        />
      </div>
    </Sheet>

    {/* L8: stacks on the menu Sheet the same way "wrong" below does — this one
        is always mounted, its own `open` toggled, rather than conditionally
        rendered: `AddToListSheet`'s interface takes `open` as a controlled
        boolean precisely so a caller can let its own close animation play
        instead of the whole thing disappearing mid-drag. */}
    <AddToListSheet entry={e} open={view === 'addlist'} onClose={() => setView('menu')} />

    {/* WP 8.4: "seen it, and a rating out of five". Conditionally rendered
        rather than always-mounted-with-`open` — see `RateSheet`'s own note
        for why that is safe here and is not `AddToListSheet`'s shape. */}
    {view === 'rate' && (
      <RateSheet e={e} ids={ids} seen={seenRow} onClose={() => setView('menu')} />
    )}

    {/* "Something wrong with this?" — a note, and no taste signal.

        Rule 4. Dismissing says "not for me" and is taste; this says the adapter
        got it wrong and is health, and the two were written to the same table
        and told apart by an action name. They are not the same kind of fact and
        the scorer should never have been reading one of them, so this writes
        the thing a person can act on — a note, in Doug's own queue, with the
        entry id in its own column — and `markWrong` only takes the row off the
        screen. The composer is `NotesComposer`, the same one the account
        sheet's Notes section uses, loaded lazily (see the import). */}
    {view === 'wrong' && (
      <Sheet open onOpenChange={o => { if (!o) setView('menu') }} title="Something wrong with this?" closeButton>
        <p className="ev-acts__note">
          {rowTitle(e)} — {e.venue ?? e.organisation ?? 'no venue'}
          {e.source_recipe ? ` · ${e.source_recipe}` : ''}
        </p>
        <Suspense fallback={null}>
          <NotesComposer
            autoFocus
            writeError={noteError}
            onAdd={async (body, kind) => {
              // The id goes in its own column, because it is counted: the
              // health strip groups this week's reports by the entry's
              // `source_recipe`, and a body cannot be joined. It is in the body
              // as well — the stamp is what the next session actually reads,
              // and it is the fields verbatim after what Doug typed, not
              // composed prose.
              const stamp = [`entry ${e.id}`, e.source_recipe, e.url].filter(Boolean).join(' · ')
              const ok = await attempt(addNote.mutateAsync({
                body: `${body}\n\n${stamp}`, kind, entryId: e.id,
              }))
              if (!ok) return false
              // The row goes away too — a wrong listing should not stay in the
              // feed while it is being fixed. No taste signal: see above.
              wrongM.mutate({ id: e.id })
              onClose()
              return true
            }}
          />
        </Suspense>
      </Sheet>
    )}
    </>
  )
}
